import React, { useState, useEffect } from 'react';
import { API } from '../App';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Calendar, DollarSign, MessageSquare, FileText, Plus, X, ClipboardList, ChevronDown, ChevronUp } from 'lucide-react';import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const AdminPortal = () => {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [families, setFamilies] = useState([]);
  const [discoveryCalls, setDiscoveryCalls] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [intakeForms, setIntakeForms] = useState([]);
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [sessionNote, setSessionNote] = useState({
    child_id: '',
    session_date: new Date().toISOString().split('T')[0],
    notes: '',
    goals_discussed: [],
    action_items: []
  });
  const [newGoal, setNewGoal] = useState('');
  const [newAction, setNewAction] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, bookingsRes, usersRes, callsRes, intakeRes] = await Promise.all([
        API.get('/admin/stats'),
        API.get('/admin/bookings'),
        API.get('/admin/users?role=parent'),
        API.get('/admin/discovery-calls'),
        API.get('/admin/intake-forms')
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data.bookings || []);
      setDiscoveryCalls(callsRes.data || []);
      setIntakeForms(intakeRes.data || []);
      
      // Enrich families with children data
      const familiesData = await Promise.all(
        (usersRes.data.users || []).map(async (user) => {
          const childrenRes = await API.get(`/children`).catch(() => ({ data: [] }));
          const userChildren = childrenRes.data?.filter(c => c.user_id === user.user_id) || [];
          return { ...user, children: userChildren };
        })
      );
      setFamilies(familiesData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await API.put(`/bookings/${bookingId}/status?status=${status}`);
      toast.success(`Session ${status}!`);
      loadData();
    } catch (error) {
      toast.error('Failed to update session');
    }
  };

  const handleAddSessionNote = async (e) => {
    e.preventDefault();
    try {
      await API.post('/coaching-notes', sessionNote);
      toast.success('Session note saved!');
      setAddNoteOpen(false);
      setSessionNote({
        child_id: '',
        session_date: new Date().toISOString().split('T')[0],
        notes: '',
        goals_discussed: [],
        action_items: []
      });
      setSelectedBooking(null);
      setNewGoal('');
      setNewAction('');
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  const addGoalToNote = () => {
    if (newGoal.trim()) {
      setSessionNote(prev => ({
        ...prev,
        goals_discussed: [...prev.goals_discussed, newGoal.trim()]
      }));
      setNewGoal('');
    }
  };

  const addActionToNote = () => {
    if (newAction.trim()) {
      setSessionNote(prev => ({
        ...prev,
        action_items: [...prev.action_items, newAction.trim()]
      }));
      setNewAction('');
    }
  };

  const removeFromArray = (field, index) => {
    setSessionNote(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const openNoteForBooking = (booking) => {
    setSelectedBooking(booking);
    // Pre-populate with booking date
    setSessionNote({
      child_id: '',
      session_date: booking.date,
      notes: '',
      goals_discussed: [],
      action_items: []
    });
    setAddNoteOpen(true);
  };

  const updateDiscoveryCallStatus = async (callId, status, notes = '') => {
    try {
      await API.put(`/admin/discovery-calls/${callId}?status=${status}&notes=${encodeURIComponent(notes)}`);
      toast.success(`Discovery call ${status}!`);
      loadData();
    } catch (error) {
      toast.error('Failed to update discovery call');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-green-100 text-green-700',
      completed: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getSessionLabel = (type) => {
    const labels = {
      quick_15: '15 min',
      standard_30: '30 min',
      deep_60: '60 min'
    };
    return labels[type] || type;
  };

  // Calculate revenue
  const totalRevenue = bookings
    .filter(b => b.status === 'completed')
    .reduce((sum, b) => sum + (b.price || 0), 0);

  const pendingRevenue = bookings
    .filter(b => b.status === 'confirmed')
    .reduce((sum, b) => sum + (b.price || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const upcomingBookings = bookings.filter(b => ['pending', 'confirmed'].includes(b.status));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold mb-2">Business Dashboard</h1>
        <p className="text-muted-foreground">Manage your coaching practice</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-2">
              <Users className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold">{families.length}</CardTitle>
            <CardDescription>Active Families</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mb-2">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold">{upcomingBookings.length}</CardTitle>
            <CardDescription>Upcoming Sessions</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-2">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold">${totalRevenue}</CardTitle>
            <CardDescription>Revenue (Completed)</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mb-2">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold">${pendingRevenue}</CardTitle>
            <CardDescription>Pending Revenue</CardDescription>
          </CardHeader>
        </Card>
      </div>

      <Tabs defaultValue="calendar">
        <TabsList>
          <TabsTrigger value="calendar">📅 Calendar</TabsTrigger>
          <TabsTrigger value="discovery">Discovery Calls</TabsTrigger>
          <TabsTrigger value="schedule">Schedule & Bookings</TabsTrigger>
          <TabsTrigger value="families">Families</TabsTrigger>
          <TabsTrigger value="intake" data-testid="admin-intake-tab">Intake Forms</TabsTrigger>
          <TabsTrigger value="notes">Session Notes</TabsTrigger>
        </TabsList>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Your Schedule</CardTitle>
                  <CardDescription>View all sessions and discovery calls</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Badge className="bg-yellow-500">Pending</Badge>
                  <Badge className="bg-green-500">Confirmed</Badge>
                  <Badge className="bg-indigo-500">Completed</Badge>
                  <Badge className="bg-purple-500">Discovery</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* List View Schedule */}
              <div className="space-y-4">
                {bookings
                  .sort((a, b) => new Date(a.date + ' ' + a.time) - new Date(b.date + ' ' + b.time))
                  .map(booking => (
                    <Card key={booking.booking_id} className="bg-gray-50">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="text-center">
                              <div className="text-2xl font-bold">{new Date(booking.date).getDate()}</div>
                              <div className="text-xs text-muted-foreground uppercase">
                                {new Date(booking.date).toLocaleDateString('en-US', { month: 'short' })}
                              </div>
                            </div>
                            <div>
                              <h3 className="font-semibold">{booking.user_name}</h3>
                              <p className="text-sm text-muted-foreground">{booking.time} • {getSessionLabel(booking.session_type)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={getStatusColor(booking.status)}>
                              {booking.status}
                            </Badge>
                            <span className="text-lg font-bold text-primary">${booking.price}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                
                {discoveryCalls
                  .filter(c => c.status === 'scheduled')
                  .map(call => (
                    <Card key={call.call_id} className="bg-purple-50 border-purple-200">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center text-white">
                              <Calendar className="w-6 h-6" />
                            </div>
                            <div>
                              <h3 className="font-semibold">Discovery Call: {call.parent_name}</h3>
                              <p className="text-sm text-muted-foreground">
                                {call.child_name} ({call.child_age}y) • {call.best_time_to_call}
                              </p>
                            </div>
                          </div>
                          <Badge className="bg-purple-600">Discovery</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                
                {bookings.length === 0 && discoveryCalls.filter(c => c.status === 'scheduled').length === 0 && (
                  <div className="text-center py-12">
                    <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-muted-foreground">No scheduled sessions yet</p>
                  </div>
                )}
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Google Calendar Integration
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Connect your Google Calendar to sync your Unbox'd sessions automatically. All confirmed sessions will appear in your Google Calendar.
                </p>
                <Button variant="outline" size="sm" disabled>
                  <Calendar className="w-4 h-4 mr-2" />
                  Connect Google Calendar (Coming Soon)
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Discovery Calls Tab */}
        <TabsContent value="discovery" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Discovery Call Requests</CardTitle>
              <CardDescription>New inquiries from potential families</CardDescription>
            </CardHeader>
            <CardContent>
              {discoveryCalls.length > 0 ? (
                <div className="space-y-4">
                  {discoveryCalls.map(call => (
                    <Card key={call.call_id} className={`${call.status === 'new' ? 'border-2 border-green-500' : 'bg-gray-50'}`}>
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {call.status === 'new' && <Badge className="bg-green-600">NEW</Badge>}
                              <h3 className="font-semibold text-lg">{call.parent_name}</h3>
                              <Badge variant="outline" className="capitalize">{call.status}</Badge>
                            </div>
                            <div className="space-y-1 text-sm">
                              <p className="text-muted-foreground">📧 {call.email}</p>
                              <p className="text-muted-foreground">📞 {call.phone}</p>
                              <p className="text-xs text-muted-foreground">
                                Requested {formatDistanceToNow(new Date(call.created_at), { addSuffix: true })}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4 bg-white p-4 rounded border">
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Child's Name</p>
                            <p className="font-medium">{call.child_name}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Age</p>
                            <p className="font-medium">{call.child_age} years old</p>
                          </div>
                        </div>

                        <div className="mb-4 p-4 bg-white rounded border">
                          <p className="text-sm font-medium text-muted-foreground mb-2">Main Concerns:</p>
                          <p className="text-sm">{call.main_concerns}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Best Time to Call</p>
                            <p className="text-sm">{call.best_time_to_call}</p>
                          </div>
                          {call.heard_about_us && (
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Heard About Us</p>
                              <p className="text-sm capitalize">{call.heard_about_us}</p>
                            </div>
                          )}
                        </div>

                        {call.notes && (
                          <div className="mb-4 p-3 bg-yellow-50 rounded border border-yellow-200">
                            <p className="text-sm font-medium text-muted-foreground mb-1">Your Notes:</p>
                            <p className="text-sm">{call.notes}</p>
                          </div>
                        )}

                        <div className="flex gap-2 flex-wrap">
                          {call.status === 'new' && (
                            <>
                              <Button 
                                size="sm" 
                                onClick={() => updateDiscoveryCallStatus(call.call_id, 'contacted')}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                Mark as Contacted
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  const notes = prompt('Add notes about this family:');
                                  if (notes) updateDiscoveryCallStatus(call.call_id, 'contacted', notes);
                                }}
                              >
                                Add Notes
                              </Button>
                            </>
                          )}
                          {call.status === 'contacted' && (
                            <>
                              <Button 
                                size="sm"
                                onClick={() => updateDiscoveryCallStatus(call.call_id, 'scheduled')}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Schedule Discovery Call
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => updateDiscoveryCallStatus(call.call_id, 'declined')}
                              >
                                Decline
                              </Button>
                            </>
                          )}
                          {call.status === 'scheduled' && (
                            <Button 
                              size="sm"
                              onClick={() => updateDiscoveryCallStatus(call.call_id, 'completed')}
                            >
                              Mark as Completed
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No discovery call requests yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedule Tab */}
        <TabsContent value="schedule" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Session Schedule</CardTitle>
                  <CardDescription>Manage coaching sessions and payment tracking</CardDescription>
                </div>
                <Button onClick={() => setAddNoteOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Session Note
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {bookings.length > 0 ? (
                <div className="space-y-4">
                  {bookings.map(booking => (
                    <Card key={booking.booking_id} className="bg-gray-50">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{booking.user_name}</h3>
                              <Badge className={getStatusColor(booking.status)}>
                                {booking.status}
                              </Badge>
                              <Badge variant="outline">{getSessionLabel(booking.session_type)}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{booking.user_email}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-primary">${booking.price}</p>
                            <p className="text-xs text-muted-foreground">
                              {booking.status === 'completed' ? 'Paid offline' : 'Payment offline'}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Date & Time</p>
                            <p className="font-medium">{booking.date} at {booking.time}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Requested</p>
                            <p className="font-medium text-sm">
                              {formatDistanceToNow(new Date(booking.created_at), { addSuffix: true })}
                            </p>
                          </div>
                        </div>

                        {booking.notes && (
                          <div className="mb-4 p-3 bg-white rounded border">
                            <p className="text-sm font-medium text-muted-foreground mb-1">Parent Notes:</p>
                            <p className="text-sm">{booking.notes}</p>
                          </div>
                        )}

                        <div className="flex gap-2 flex-wrap">
                          {booking.status === 'pending' && (
                            <>
                              <Button 
                                size="sm" 
                                onClick={() => updateBookingStatus(booking.booking_id, 'confirmed')}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Confirm
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => updateBookingStatus(booking.booking_id, 'cancelled')}
                              >
                                Decline
                              </Button>
                            </>
                          )}
                          {booking.status === 'confirmed' && (
                            <>
                              <Button 
                                size="sm" 
                                onClick={() => updateBookingStatus(booking.booking_id, 'completed')}
                              >
                                Mark Completed
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openNoteForBooking(booking)}
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                Add Session Note
                              </Button>
                            </>
                          )}
                          {booking.status === 'completed' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openNoteForBooking(booking)}
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              View/Edit Note
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No sessions scheduled yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Families Tab */}
        <TabsContent value="families" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Your Families</CardTitle>
              <CardDescription>Parents and children you're working with</CardDescription>
            </CardHeader>
            <CardContent>
              {families.length > 0 ? (
                <div className="space-y-4">
                  {families.map(family => (
                    <Card key={family.user_id} className="bg-gray-50">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="font-semibold text-lg">{family.name}</h3>
                            <p className="text-sm text-muted-foreground">{family.email}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Member since {new Date(family.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => setSelectedFamily(family)}>
                            View Details
                          </Button>
                        </div>

                        {family.children && family.children.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="text-sm font-medium mb-2">Children:</p>
                            <div className="flex flex-wrap gap-2">
                              {family.children.map(child => (
                                <Link key={child.child_id} to={`/child/${child.child_id}`}>
                                  <Badge variant="outline" className="cursor-pointer hover:bg-primary hover:text-primary-foreground">
                                    {child.name} ({child.age}y)
                                  </Badge>
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Quick stats for this family */}
                        <div className="grid grid-cols-3 gap-4 mt-4">
                          <div className="text-center p-2 bg-white rounded">
                            <p className="text-2xl font-bold text-primary">
                              {bookings.filter(b => b.user_id === family.user_id && b.status === 'completed').length}
                            </p>
                            <p className="text-xs text-muted-foreground">Completed Sessions</p>
                          </div>
                          <div className="text-center p-2 bg-white rounded">
                            <p className="text-2xl font-bold text-green-600">
                              {bookings.filter(b => b.user_id === family.user_id && b.status === 'confirmed').length}
                            </p>
                            <p className="text-xs text-muted-foreground">Upcoming</p>
                          </div>
                          <div className="text-center p-2 bg-white rounded">
                            <p className="text-2xl font-bold text-purple-600">
                              ${bookings.filter(b => b.user_id === family.user_id && b.status === 'completed')
                                .reduce((sum, b) => sum + b.price, 0)}
                            </p>
                            <p className="text-xs text-muted-foreground">Total Revenue</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No families enrolled yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* Intake Forms Tab */}
        <TabsContent value="intake" className="space-y-4" data-testid="admin-intake-content">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Client Intake Forms</CardTitle>
                  <CardDescription>Review submitted intake forms from families</CardDescription>
                </div>
                <Badge variant="outline" className="text-sm">{intakeForms.length} submitted</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {intakeForms.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No intake forms submitted yet.</p>
              ) : (
                <div className="space-y-3">
                  {intakeForms.map(form => (
                    <Card key={form.form_id} className={`cursor-pointer transition-colors ${selectedIntake?.form_id === form.form_id ? 'border-[#C97B5A]' : 'hover:bg-gray-50'}`}>
                      <CardContent className="pt-4 pb-4">
                        <div
                          className="flex items-center justify-between"
                          onClick={() => setSelectedIntake(selectedIntake?.form_id === form.form_id ? null : form)}
                          data-testid={`intake-row-${form.form_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#C97B5A]/10 flex items-center justify-center">
                              <ClipboardList className="w-5 h-5 text-[#C97B5A]" />
                            </div>
                            <div>
                              <h3 className="font-semibold">{form.parentName || 'Unknown'}</h3>
                              <p className="text-sm text-gray-500">
                                Child: {form.childFirstName} {form.childLastName || ''} {form.childAge ? `(${form.childAge}y)` : ''}
                                {form.created_at && <span className="ml-2">• {new Date(form.created_at).toLocaleDateString()}</span>}
                              </p>
                            </div>
                          </div>
                          {selectedIntake?.form_id === form.form_id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                        </div>

                        {selectedIntake?.form_id === form.form_id && (
                          <div className="mt-4 border-t pt-4 space-y-6" data-testid="intake-detail">
                            <IntakeSection title="Family & Child Info" items={[
                              { l: 'Parent/Guardian', v: form.parentName },
                              { l: 'Relationship', v: form.relationship },
                              { l: 'Phone', v: form.phone },
                              { l: 'Location', v: form.address },
                              { l: 'Child', v: `${form.childFirstName || ''} ${form.childLastName || ''}`.trim() },
                              { l: 'DOB', v: form.childDob },
                              { l: 'Age', v: form.childAge },
                              { l: 'Pronouns', v: form.childPronouns },
                              { l: 'Household', v: form.householdMembers },
                              { l: 'Siblings', v: form.siblings },
                              { l: 'Family Notes', v: form.familyNotes },
                            ]} />
                            <IntakeSection title="Diagnoses & Development" items={[
                              { l: 'Diagnoses', v: form.diagnoses?.join(', ') },
                              { l: 'Other Diagnosis', v: form.diagnosisOther },
                              { l: 'Age at Diagnosis', v: form.ageAtDiagnosis },
                              { l: 'Diagnosing Provider', v: form.diagnosingProvider },
                              { l: 'Evaluations', v: form.evaluations?.join(', ') },
                              { l: 'Evaluation Results', v: form.evaluationResults },
                              { l: 'Therapies', v: form.therapies?.join(', ') },
                              { l: 'Therapy Details', v: form.therapyDetails },
                              { l: 'Gestational Age', v: form.gestationalAge },
                              { l: 'Birth Complications', v: form.birthComplications },
                              { l: 'NICU Stay', v: form.nicuStay },
                              { l: 'Age Walked', v: form.ageWalked },
                              { l: 'Age First Words', v: form.ageFirstWords },
                              { l: 'Regression', v: form.regression },
                              { l: 'Regression Details', v: form.regressionDetails },
                              { l: 'Family History', v: form.familyHistory },
                            ]} />
                            <IntakeSection title="Medical & Health" items={[
                              { l: 'Current Specialists', v: form.currentSpecialists },
                              { l: 'GI Symptoms', v: form.giSymptoms?.join(', ') },
                              { l: 'Bedtime', v: form.sleepBedtime },
                              { l: 'Sleep Hours', v: form.sleepHours },
                              { l: 'Night Waking', v: form.nightWaking },
                              { l: 'Sleep Aids', v: form.sleepAids },
                              { l: 'Immune/Inflammatory History', v: form.immuneHistory },
                              { l: 'Food Allergies', v: form.foodAllergies },
                              { l: 'Current Diet', v: form.currentDiet },
                              { l: 'Environmental Concerns', v: form.environmentalConcerns },
                            ]} />
                            <IntakeSection title="School & Learning" items={[
                              { l: 'School', v: form.schoolName },
                              { l: 'Grade', v: form.grade },
                              { l: 'Type', v: form.schoolType },
                              { l: 'IEP/504', v: form.iepStatus },
                              { l: 'IEP Goals', v: form.iepGoals },
                              { l: 'Services', v: form.schoolServices?.join(', ') },
                              { l: 'Accommodations', v: form.accommodations?.join(', ') },
                              { l: 'Accommodation Details', v: form.accommodationDetails },
                              { l: 'Academic Strengths', v: form.academicStrengths },
                              { l: 'Academic Challenges', v: form.academicChallenges },
                            ]} />
                            <IntakeSection title="Behavior & Regulation" items={[
                              { l: 'Concerns', v: form.behaviorConcerns?.join(', ') },
                              { l: 'Frequency', v: form.behaviorFrequency },
                              { l: 'Triggers', v: form.knownTriggers },
                              { l: 'Strategies Tried', v: form.strategiesTried },
                              { l: 'Sensory Profile', v: form.sensoryProfile },
                              { l: 'Sensory Details', v: form.sensoryDetails },
                              { l: 'Meltdown Description', v: form.meltdownDescription },
                              { l: 'What Helps', v: form.meltdownHelps },
                              { l: 'Strengths & Interests', v: form.childStrengths },
                            ]} />
                            <IntakeSection title="Communication" items={[
                              { l: 'Verbal Level', v: form.verbalLevel },
                              { l: 'Receptive Language', v: form.receptiveLanguage },
                              { l: 'Expressive Concerns', v: form.expressiveConcerns },
                              { l: 'AAC Use', v: form.aacUse },
                              { l: 'AAC Details', v: form.aacDetails },
                              { l: 'Social Communication', v: form.socialCommunication },
                              { l: 'SLP History', v: form.slpHistory },
                            ]} />
                            <IntakeSection title="Medications & Supplements" items={[
                              { l: 'Medications', v: form.medications },
                              { l: 'Supplements', v: form.supplements?.join(', ') },
                              { l: 'Dietary Protocol', v: form.dietaryProtocol },
                            ]} />
                            <IntakeSection title="Why Unbox'D" items={[
                              { l: 'How Heard', v: form.howHeard },
                              { l: 'Why Now', v: form.whyNow },
                              { l: 'Top Goals', v: form.topGoals },
                              { l: 'Timeline', v: form.timeline },
                              { l: 'Additional Notes', v: form.anythingElse },
                            ]} />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Session Notes Tab */}
        <TabsContent value="notes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Quick Access: Add Session Note</CardTitle>
              <CardDescription>Document coaching sessions for your families</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                You can add session notes from the Schedule tab or go directly to a child's dashboard to add detailed notes.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {families.map(family => 
                  family.children?.map(child => (
                    <Link key={child.child_id} to={`/child/${child.child_id}`}>
                      <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                        <CardContent className="pt-6">
                          <h3 className="font-semibold mb-1">{child.name}</h3>
                          <p className="text-sm text-muted-foreground">{family.name}</p>
                          <Button variant="outline" size="sm" className="w-full mt-3">
                            View Dashboard & Add Notes
                          </Button>
                        </CardContent>
                      </Card>
                    </Link>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Session Note Dialog */}
      <Dialog open={addNoteOpen} onOpenChange={setAddNoteOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Session Note</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSessionNote} className="space-y-4">
            <div>
              <Label>Select Child</Label>
              <Select
                value={sessionNote.child_id}
                onValueChange={(value) => setSessionNote({ ...sessionNote, child_id: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a child" />
                </SelectTrigger>
                <SelectContent>
                  {families.map(family => 
                    family.children?.map(child => (
                      <SelectItem key={child.child_id} value={child.child_id}>
                        {child.name} - {family.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="session_date">Session Date</Label>
              <Input
                id="session_date"
                type="date"
                value={sessionNote.session_date}
                onChange={(e) => setSessionNote({ ...sessionNote, session_date: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="notes">Session Notes</Label>
              <Textarea
                id="notes"
                rows={6}
                placeholder="What did we discuss? What progress was made?"
                value={sessionNote.notes}
                onChange={(e) => setSessionNote({ ...sessionNote, notes: e.target.value })}
                required
              />
            </div>

            <div>
              <Label>Goals Discussed</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {sessionNote.goals_discussed.map((goal, idx) => (
                  <Badge key={idx} className="pl-3 pr-2">
                    {goal}
                    <button type="button" onClick={() => removeFromArray('goals_discussed', idx)} className="ml-2">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a goal"
                  value={newGoal}
                  onChange={(e) => setNewGoal(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addGoalToNote())}
                />
                <Button type="button" variant="outline" onClick={addGoalToNote}>
                  Add
                </Button>
              </div>
            </div>

            <div>
              <Label>Action Items for Parent</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {sessionNote.action_items.map((action, idx) => (
                  <Badge key={idx} variant="outline" className="pl-3 pr-2">
                    {action}
                    <button type="button" onClick={() => removeFromArray('action_items', idx)} className="ml-2">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add an action item"
                  value={newAction}
                  onChange={(e) => setNewAction(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addActionToNote())}
                />
                <Button type="button" variant="outline" onClick={addActionToNote}>
                  Add
                </Button>
              </div>
            </div>

            <Button type="submit" className="w-full">Save Session Note</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const IntakeSection = ({ title, items }) => {
  const filledItems = items.filter(i => i.v && i.v.toString().trim());
  if (filledItems.length === 0) return null;
  return (
    <div>
      <h4 className="text-sm font-semibold text-[#C97B5A] uppercase tracking-wider mb-2">{title}</h4>
      <div className="grid gap-2">
        {filledItems.map(({ l, v }) => (
          <div key={l} className="grid grid-cols-[140px_1fr] gap-2 text-sm">
            <span className="text-gray-500 font-medium">{l}</span>
            <span className="text-gray-800 whitespace-pre-wrap">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPortal;
